import { create } from 'zustand';
import { evalStntApi } from '@/shared/api/eval/eval-stnt';
import { sendToKvAssessmentView } from '@/shared/ui';
import datetime from '@/shared/utils/datetime';
import { sendTraceLog } from '@/shared/log/tracker';

type StorageType = 'local' | 'session';
type EvalIemMap = { [evlIemId: number]: Evalution.EvlIemResult };

export interface EvaluationStore {
    condition: API.EvalCondition;
    gubun: string;
    sUserId: string;
    setSUserId: (id: string) => void;
    sUserNickName: string;
    setSUserNickName: (name: string) => void;
    evalIemList: Evalution.EvlIemResult[];
    setEvalIemList: (list: Evalution.EvlIemResult[]) => void;
    evalIemMap: EvalIemMap;
    setEvalIemMap: (map: EvalIemMap) => void;
    isStarted: boolean;
    setIsStarted: (started: boolean) => void;
    showEvalView: boolean;
    setShowEvalView: (show: boolean) => void;
    currIdx: number;
    setCurrIdx: (index: number) => void;
    publishInfo: { publishId: number; trgtId: number };
    setPublishInfo: (info: { publishId: number; trgtId: number }) => void;
    sCurrentTime: string;
    setSCurrentTime: (time: string) => void;
    sTextbookId?: number;
    setSTextbookId: (id?: number) => void;
    info?: API.STNT.EvalInfo.Res;
    setInfo: (info?: API.STNT.EvalInfo.Res) => void;
    viewerInfo?: KvAssessmentViewProps;
    setViewerInfo: (info?: KvAssessmentViewProps) => void;
    evalResult?: API.STNT.EvalResult.Res;
    setEvalResult: (result?: API.STNT.EvalResult.Res) => void;
    startTime?: string;
    setStartTime: (time?: string) => void;
    addTime?: number;
    setAddTime: (time?: number) => void;
    vivaTcUserId?: string;
    setVivaTcUserId: (id: string) => void;

    taskIemMap: { [taskIemId: number]: taskItemInfo };
    initEvaluation: (evlId?: number) => Promise<API.STNT.EvalInfo.Res | undefined>;
    startEvaluation: (evlId: number, startTime?: string, addTime?: number) => Promise<boolean>;
    setEvalInfo: (info: API.STNT.EvalInfo.Res, startTime: string, addSec?: number) => Promise<void>;
    setArticle: (index: number) => Promise<void>;
    saveArticle: (detail: any) => Promise<void>;
    submit: (detail: any, submAt?: Evalution.FlagYn) => Promise<void>;
    useTime?: number;
    startCurrentTime?: string;
}

const getStorage = (type: StorageType) => {
    return type === 'session' ? sessionStorage : localStorage;
};
const storage = (type: StorageType) => {
    const _storage = getStorage(type);
    return {
        set: (key: string, data: any) => {
            if (data !== undefined) {
                _storage.setItem(key, JSON.stringify(data));
            }
        },
        get: <T>(key: string, defaultVal: T): T => {
            const data = _storage.getItem(key);
            return data ? (JSON.parse(data) as T) : defaultVal;
        },
    };
};

const local = storage('local');
const session = storage('session');

export const useEvaluationStore = create<EvaluationStore>((set, get) => ({
    condition: '',
    gubun: 'S',
    sUserId: '',
    taskIemMap: {},
    setSUserId: (id) => set({ sUserId: id }),
    sUserNickName: '',
    setSUserNickName: (name) => set({ sUserNickName: name }),
    evalIemList: [],
    setEvalIemList: (list) => set({ evalIemList: list }),
    evalIemMap: {},
    setEvalIemMap: (map) => set({ evalIemMap: map }),
    isStarted: false,
    setIsStarted: (started) => set({ isStarted: started }),
    showEvalView: false,
    setShowEvalView: (show) => set({ showEvalView: show }),
    currIdx: 0,
    setCurrIdx: (index) => set({ currIdx: index }),
    publishInfo: { publishId: 0, trgtId: 0 },
    setPublishInfo: (info) => set({ publishInfo: info }),
    sCurrentTime: '',
    setSCurrentTime: (time) => set({ sCurrentTime: time }),
    sTextbookId: undefined,
    setSTextbookId: (id) => set({ sTextbookId: id }),
    info: undefined,
    setInfo: (info) => set({ info }),
    viewerInfo: undefined,
    setViewerInfo: (info) => set({ viewerInfo: info }),
    evalResult: undefined,
    setEvalResult: (result) => set({ evalResult: result }),
    startTime: undefined,
    setStartTime: (time) => set({ startTime: time }),
    addTime: undefined,
    setAddTime: (time) => set({ addTime: time }),
    setVivaTcUserId: (id) => set({ vivaTcUserId: id }),

    initEvaluation: async (evlId?: number): Promise<API.STNT.EvalInfo.Res | undefined> => {
        set({
            isStarted: false,
            showEvalView: false,
            viewerInfo: undefined,
            evalResult: undefined,
            evalIemList: [],
            evalIemMap: {},
            startTime: undefined,
            addTime: undefined,
            currIdx: 0,
            useTime: undefined,
            startCurrentTime: undefined,
        });

        if (evlId) {
            const res = await evalStntApi.getStEvalInfo({ evlId, userId: get().sUserId });
            if (res) {
                set({ info: res, addTime: res.evlAdiSec, startTime: res.evlPrgDt });
                return res;
            }
        }
        return undefined;
    },

    startEvaluation: async (
        evlId: number,
        startTime?: string,
        addTime?: number
    ): Promise<boolean> => {
        if (!evlId) return false;
        const res = await evalStntApi.getStEvalInfo({ evlId, userId: get().sUserId });
        if (res) {
            const resultRes = await evalStntApi.getStEvalResult(
                { evlId, userId: get().sUserId },
                false
            );
            if (resultRes) {
                if (res.submAt === 'N' && resultRes.eakSttsCd > 1) {
                    await get().initEvaluation();
                    set({
                        isStarted: true,
                        evalResult: resultRes,
                        evalIemMap: Object.fromEntries(
                            resultRes.evalIemList.map((e) => [e.evlIemId, e])
                        ),
                        evalIemList: resultRes.evalIemList,
                        showEvalView: true,
                    });
                    await get().setEvalInfo(
                        res,
                        res.eakStDt || startTime || get().sCurrentTime,
                        addTime
                    );
                    return true;
                }
            }
        }
        return false;
    },

    setEvalInfo: async (
        info: API.STNT.EvalInfo.Res,
        startTime: string,
        addSec?: number
    ): Promise<void> => {
        // 사용 시간
        const timer = await evalStntApi.getEvalTimeUsage({
            targetId: info.id,
            studentId: get().sUserId,
        });

        set({
            viewerInfo: {
                startTime,
                timTime: info.timTime,
                addSec,
                info,
                endTime: info.evlCpDt,
            },
            startCurrentTime: datetime.getCurrTime(),
            startTime,
            addTime: addSec,
            useTime: timer?.resultData ?? 0,
        });
    },

    setArticle: async (index: number): Promise<void> => {
        set({ currIdx: index });

        const userId = get().sUserId;
        const info = get().viewerInfo?.info;
        const evalIem = get().evalIemList[index];
        if (get().isStarted && info && evalIem) {
            // 평가 진행중일때 recheck 호출
            const evlId = info.id;
            const evlIemId = evalIem.evlIemId;
            const subId = evalIem.subId;
            await evalStntApi.postStEvalRecheck({ evlId, userId, evlIemId, subId }, false);
        }
    },

    saveArticle: async (detail: any): Promise<void> => {
        // Save logic

        const info = get().viewerInfo?.info;
        if (info && get().isStarted && detail && detail.res && detail.res.length > 0) {
            const {
                article_id,
                article_sub_id: subId,
                is_correct,
                answer,
                image,
                time: evlTime,
                is_hint_tutor,
            } = detail.res[0];
            const result = get().evalIemMap[article_id];
            if (result) {
                const { id: evlId } = info;
                const { evlResultId, evlIemId } = result;
                let errata = 0;
                switch (is_correct) {
                    case 'O':
                        errata = 1;
                        break; // 정답
                    case 'X':
                        errata = 2;
                        break; // 오답
                    case 'N':
                        errata = 3;
                        break; // 부분정답
                    default:
                        errata = 4;
                        break; // 채점불가
                }
                const subMitAnw = JSON.stringify(answer);
                let subMitAnwUrl = '';
                if (image && image[0] && image[0].url) {
                    subMitAnwUrl = image[0].url;
                }
                const hntUseAt = is_hint_tutor && is_hint_tutor.toString() == 'true' ? 'Y' : 'N';
                const res = await evalStntApi.postStEvalSave(
                    {
                        evlId,
                        evlIemId,
                        evlResultId,
                        subId,
                        errata,
                        subMitAnw,
                        subMitAnwUrl,
                        evlTime,
                        userId: get().sUserId,
                        hntUseAt,
                    },
                    false
                );
                sendTraceLog('', '', '', 'debug', '[SBANK_LOG] postStEvalSave res =', res);
            }
        }
    },

    submit: async (detail: any, submAt: Evalution.FlagYn = 'Y'): Promise<void> => {
        await get().saveArticle(detail);
        const info = get().viewerInfo?.info;
        if (info && get().isStarted) {
            // 제출 하기
            const res = await evalStntApi.postStEvalSubmit(
                {
                    evlId: info.id,
                    userId: get().sUserId,
                    submAt,
                },
                false
            );
            sendTraceLog('', '', '', 'debug', '[SBANK_LOG] postStEvalSubmit res = ', res);
            let msg: any = { success: false, errmsg: 'API Error!' };
            if (res) {
                set({ isStarted: false });
                msg = { success: res.resultOk, errmsg: res.resultMsg, mode: 'none' };
                sendToKvAssessmentView(msg, 'notify-user-report');

                setTimeout(() => {
                    location.replace('/eval/complete');
                }, 200);
            }
            sendToKvAssessmentView(msg, 'notify-user-report');
        }
    },
}));

export default useEvaluationStore;
